import type { Order } from '@modules/OrderPreparation/PackingV2/models/Packing';
import { getOrderAllProductSummaries, getOrderProductQtyTotal } from './orderHasProduct';

/** Pedido cuyas líneas son únicamente el producto del descuadre */
export const isOrderOnlyDiscrepancyProduct = (order: Order, productId: number): boolean => {
  const lines = getOrderAllProductSummaries(order);
  return lines.length > 0 && lines.every((line) => line.product_id === productId);
};

const parseOrderDate = (value: string | null | undefined): number => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

type RankedOrder = {
  order: Order;
  productQty: number;
  isSingleProduct: boolean;
  createdAt: number;
};

/** Desempate: mono-producto, luego más nuevo */
const compareSingleProductThenNewest = (a: RankedOrder, b: RankedOrder): number => {
  if (a.isSingleProduct !== b.isSingleProduct) {
    return a.isSingleProduct ? -1 : 1;
  }
  return b.createdAt - a.createdAt;
};

/**
 * Ordena candidatos para cuadrar el descuadre (uds. del producto en el pedido):
 * 1. Mejor ajuste (exacto al objetivo / mínimo exceso / mejor hueco)
 * 2. Solo ese producto en el pedido
 * 3. Más nuevo
 */
const compareForDiscrepancyFit = (a: RankedOrder, b: RankedOrder, targetQty: number, remaining?: number): number => {
  const need = remaining ?? targetQty;

  const scoreA = fitScore(a.productQty, need);
  const scoreB = fitScore(b.productQty, need);
  if (scoreA !== scoreB) return scoreA - scoreB;

  return compareSingleProductThenNewest(a, b);
};

const fitScore = (productQty: number, need: number): number => {
  if (productQty === need) return 0;
  if (productQty > need) return 100 + (productQty - need);
  return 300 + (need - productQty);
};

const pickBestCandidate = (candidates: RankedOrder[], targetQty: number, remaining?: number): RankedOrder | null => {
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => compareForDiscrepancyFit(a, b, targetQty, remaining))[0];
};

const selectGreedyToCover = (ranked: RankedOrder[], targetQty: number): Set<number> => {
  const pool = [...ranked];
  const selected = new Set<number>();
  let accumulated = 0;

  while (accumulated < targetQty && pool.length > 0) {
    const remaining = targetQty - accumulated;
    const best = pickBestCandidate(pool, targetQty, remaining);

    if (!best) break;

    selected.add(best.order.id);
    accumulated += best.productQty;
    const idx = pool.indexOf(best);
    if (idx >= 0) pool.splice(idx, 1);
  }

  return selected;
};

/**
 * Preselección (misma lógica con o sin otros productos en el pedido):
 * 1. Cuadrar descuadre (exacto → mínimo exceso → mejor combinación)
 * 2. Desempate: pedido solo con ese producto
 * 3. Desempate: más nuevo
 */
export const computeSmartOrderSelection = (orders: Order[], productId: number, targetQty: number): Set<number> => {
  if (targetQty < 1 || orders.length === 0) {
    return new Set();
  }

  const ranked: RankedOrder[] = orders
    .map((order) => ({
      order,
      productQty: getOrderProductQtyTotal(order, productId),
      isSingleProduct: isOrderOnlyDiscrepancyProduct(order, productId),
      createdAt: parseOrderDate(order.order_created_at),
    }))
    .filter((entry) => entry.productQty > 0);

  if (ranked.length === 0) {
    return new Set();
  }

  const exactMatches = ranked.filter((e) => e.productQty === targetQty);
  const exactBest = pickBestCandidate(exactMatches, targetQty);
  if (exactBest) {
    return new Set([exactBest.order.id]);
  }

  const soloCover = ranked.filter((e) => e.productQty >= targetQty);
  const soloBest = pickBestCandidate(soloCover, targetQty);
  if (soloBest) {
    return new Set([soloBest.order.id]);
  }

  return selectGreedyToCover(ranked, targetQty);
};
